/**
 * Role de copacking d'un code article : compose, composant, ou rien.
 *
 * Premiere demande de Leonie (11/09/2026) : en lisant une ligne de
 * rapprochement ou d'ecart, savoir immediatement si le code appartient au
 * copacking. C'est ce qui evite de chercher pendant dix minutes un ecart
 * code a code qui, par construction, ne peut pas exister — le compose n'existe
 * pas en comptabilite matieres (regle 15).
 *
 * **Deux sources, toutes deux valables** (regle 15) : les tables Copacking et
 * Nomenclature declarent la grappe d'avance ; les FAB observees au journal
 * disent ce qui a reellement ete fabrique, y compris ce que les tables ont
 * oublie de declarer. Le badge indique laquelle des deux le sait.
 *
 * Module **pur** : ni base, ni requete. Les deux cotes de l'application
 * derivent le meme badge de la meme regle.
 */
import { isCodproInScope } from './product-code';
/** Libelles d'ecran et d'export — une seule orthographe pour tout le produit. */
export const ROLE_COPACK_LABELS = {
    COMPOSE: 'Composé',
    COMPOSANT: 'Composant',
};
/**
 * Valeurs du filtre d'ecran. `AUCUN` est un choix a part entiere : « montre-moi
 * ce qui n'a rien a voir avec le copacking » est la question de tous les jours.
 */
export const COPACK_FILTRES = ['COMPOSE', 'COMPOSANT', 'AUCUN'];
export const COPACK_FILTRE_LABELS = {
    COMPOSE: 'Composés',
    COMPOSANT: 'Composants',
    AUCUN: 'Hors copacking',
};
/**
 * Role de chaque code cite par le copacking.
 *
 * Deux arbitrages, tenus ici pour qu'ils ne soient pas rejoues a chaque ecran :
 *
 * - **Les composants hors plage 4/5xxxxx sont ignores** : 7xxxxx designe la
 *   matiere seche et les goodies, hors perimetre de controle (regle 15). Les
 *   badger reviendrait a promettre un rapprochement qui n'existe pas.
 * - **Un composé hors plage est conserve**. Il est souvent cree a la volee, et
 *   c'est precisement le code sur lequel on veut une alerte : le masquer
 *   cacherait le cas a instruire.
 *
 * Quand un code est les deux — un composé qui entre dans un autre composé —
 * **COMPOSE l'emporte** : c'est l'information la plus lourde de consequences,
 * puisqu'elle dit que le code ne sera jamais declare en compta matieres.
 */
export function rolesCopacking(entrees) {
    const composes = new Map();
    const composants = new Map();
    const noter = (index, codpro, source) => {
        const existant = index.get(codpro);
        if (existant)
            existant.add(source);
        else
            index.set(codpro, new Set([source]));
    };
    for (const lien of entrees.liens) {
        if (lien.compose)
            noter(composes, lien.compose, 'TABLE');
        if (lien.composant && isCodproInScope(lien.composant)) {
            noter(composants, lien.composant, 'TABLE');
        }
    }
    for (const fabrication of entrees.fabrications) {
        if (fabrication.compose)
            noter(composes, fabrication.compose, 'FAB');
        for (const composant of fabrication.composants) {
            if (composant && isCodproInScope(composant))
                noter(composants, composant, 'FAB');
        }
    }
    const ordre = ['TABLE', 'FAB'];
    const trier = (sources) => ordre.filter((source) => sources.has(source));
    const roles = new Map();
    for (const [codpro, sources] of composants) {
        roles.set(codpro, { role: 'COMPOSANT', sources: trier(sources) });
    }
    // Ecrit en second : COMPOSE recouvre COMPOSANT sur un code qui serait les deux.
    for (const [codpro, sources] of composes) {
        roles.set(codpro, { role: 'COMPOSE', sources: trier(sources) });
    }
    return roles;
}
/**
 * Role d'une **cle d'ecart** — un code seul, ou les codes d'une famille.
 *
 * Une famille qui contient un composé est une grappe de copacking : c'est ce
 * qu'il faut lire d'abord, avant de constater qu'elle contient aussi des
 * composants.
 */
export function roleDeLaCle(codpros, roles) {
    let composant = false;
    for (const codpro of codpros) {
        const detail = roles.get(codpro);
        if (detail?.role === 'COMPOSE')
            return 'COMPOSE';
        if (detail?.role === 'COMPOSANT')
            composant = true;
    }
    return composant ? 'COMPOSANT' : null;
}
//# sourceMappingURL=copacking-roles.js.map